import { MutateOptions } from '@tanstack/react-query';
import { useCallback } from 'react';

import { useStorageState } from 'hooks/useStorageState';
import { AppSyncTicketsGraphQLError } from 'queries/tickets/types';
import { useTicketsCart } from 'queries/tickets/useTicketsCart';
import { useTicketsCreateCart } from 'queries/tickets/useTicketsCreateCart';
import { useTicketsReplaceCart } from 'queries/tickets/useTicketsReplaceCart';
import { TicketsPushAppField } from 'types/api/dato-extensions.types';
import {
  CartCreateInput,
  CartCreateMutation,
  CartUpdateMutation,
} from 'types/api/tickets.types';
import { LocalCartItem } from 'types/tickets.types';

interface CreateOrUpdateCartVariables {
  items: Array<LocalCartItem>;
  holdToken?: string;
}

export const useCreateOrReplaceCart = () => {
  const { createCart, isLoading: createCartLoading } = useTicketsCreateCart();
  const { replaceCart, isLoading: replaceCartLoading } =
    useTicketsReplaceCart();
  const { cart } = useTicketsCart();

  const handleCreateCartOrReplaceCart = useCallback(
    (
      { items, holdToken }: CreateOrUpdateCartVariables,
      options: MutateOptions<
        CartCreateMutation | CartUpdateMutation,
        AppSyncTicketsGraphQLError,
        CartCreateInput
      >,
    ) => {
      const mappedItems = items.map((item) => ({
        catalogueItemId: item.catalogueItemId,
        quantity: item.quantity,
        bookObjects: item.bookObjectsInfo?.map(
          (bookObject) => bookObject.label,
        ),
      }));
      // Only replace the cart if we have matching hold tokens
      if (!!cart && cart.seatedHoldToken?.token === holdToken) {
        return replaceCart({ items: mappedItems }, options);
      } else {
        return createCart({ items: mappedItems, holdToken }, options);
      }
    },
    [cart, createCart, replaceCart],
  );

  return {
    createOrReplaceCart: handleCreateCartOrReplaceCart,
    isLoading: createCartLoading || replaceCartLoading,
  };
};

export const useAppPushState = (
  eventKey: string,
  ticketsPushApp: TicketsPushAppField,
  hasTicketOffers: boolean,
) => {
  const [appPushSoftIgnoredString, setAppPushSoftIgnored] = useStorageState(
    `nf-app-push-soft-ignored-${eventKey}`,
    {
      defaultValue: 'false',
      engine: 'sessionStorage',
    },
  );
  const showAppPushDialog =
    ticketsPushApp === 'hard' ||
    (ticketsPushApp === 'soft' && appPushSoftIgnoredString !== 'true');

  return {
    showAppPushDialog: hasTicketOffers ? false : showAppPushDialog,
    setAppPushSoftIgnored,
  };
};
